import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip } from '@mui/material'
import { InvoiceOpenIndicator } from 'features/invoice-timeline'
import moment from 'moment'
import InvoiceIcon from 'templates/Invoice/components/InvoiceIcon'
import {
  DataCustomerCharges,
  StatisticsMapperParams
} from 'templates/v2/Charge/components/ChargeTable/ChargeTable'

import { ButtonRounded } from 'components/button'
import Can from 'components/Can'
import { Info } from 'components/InfoList/InfoList'
import CustomerAvatar from 'components/v2/CustomerAvatar'

import { getInvoiceUrl } from 'utils/domain-urls'
import logError from 'utils/errorLogger'
import { formatDate } from 'utils/FormatDate'
import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

import { ToggleModalsParams } from '../../Charge'
import * as S from './ChargeTable.styles'

export const getCustomerStageLabel = (stage: string) => {
  switch (stage) {
    case 'lead':
      return 'Lead'
    case 'active_customer':
    case 'active':
      return 'Ativo'
    case 'inactive_customer':
    case 'inactive':
      return 'Inativo'
    case 'churned':
      return 'Churned'
    case 'lost':
      return 'Lost'
    case 'archived_customer':
      return 'Arquivado'
    default:
      return ''
  }
}

export const getStatusOptionCharge = (status: string, dueDate?: string) => {
  const today = moment().startOf('day').format('YYYY-MM-DD')
  switch (status) {
    case 'waiting_contract':
      return {
        label: 'Aguardando Contrato',
        color: theme.palette.alert[300]
      }
    case 'pending':
      return {
        label: 'Pendente',
        color: theme.palette.neutral[700],
        disabled: true
      }
    case 'created_payment':
      return {
        label: 'Pendente',
        color: theme.palette.neutral[700],
        disabled: true
      }
    case 'open_payment':
      if (dueDate && moment(dueDate).isBefore(today)) {
        return {
          label: 'Atrasada',
          color: theme.palette.alert[300]
        }
      }
      return {
        label: 'Aberta',
        color: theme.palette.secondary[300]
      }
    case 'no_payment':
      return {
        label: 'Expirada',
        color: theme.palette.error[300]
      }
    case 'expiring':
      return {
        color: theme.palette.error[400],
        label: 'Expirada'
      }
    case 'paid':
      return {
        label: 'Paga',
        color: theme.palette.primary.main
      }
    case 'cancelling':
      return {
        label: 'Cancelada',
        color: theme.palette.error[300]
      }
    case 'cancelled':
      return {
        label: 'Cancelada',
        color: theme.palette.error[300]
      }
    case 'expired':
      return {
        label: 'Expirada',
        color: theme.palette.error[300]
      }
    case 'suspended':
      return {
        label: 'Suspensa',
        color: theme.palette.neutral[700]
      }
    default:
      logError(`Erro ao mapear status da tabela cobranças: ${status}`)

      return {
        label: 'Status desconhecido',
        color: theme.palette.neutral[300]
      }
  }
}

const getStatusOption = (status: string, nfsePolicy?: string) => {
  switch (status) {
    case 'scheduled':
      if (nfsePolicy === 'on_billing_issued') {
        return {
          label: 'Será emitida quando a fatura abrir para pagamento',
          color: theme.palette.alert[300]
        }
      }
      if (nfsePolicy === 'on_billing_paid') {
        return {
          label: 'Será emitida quando paga',
          color: theme.palette.alert[300]
        }
      }
      return {
        label: 'Programada para emissão',
        color: theme.palette.alert[300]
      }
    case 'issued':
      return {
        label: 'Emitida',
        color: theme.palette.primary.main
      }
    case 'not_scheduled':
      return {
        label: 'Não está programada',
        color: theme.palette.neutral[500]
      }
    case 'failed':
      return {
        label: 'Erro ao emitir',
        color: theme.palette.error[300]
      }
    default:
      logError(
        `Erro ao mapear status da tabela cobrança: ${status} (getStatusOption)`
      )
      return {
        label: 'Status desconhecido',
        color: theme.palette.neutral[300]
      }
  }
}

type CustomerChargesTableMapperProps = {
  data: DataCustomerCharges
  actions: {
    toggleModals?: (params: ToggleModalsParams) => void
    AUTHORIZATION?: any
    notViewActions?: boolean
  }
  customerPage?: boolean
  isScheduledTab?: boolean
  showCustomerStage?: boolean
}

export const customerChargesTableMapper = ({
  data,
  actions,
  customerPage,
  isScheduledTab,
  showCustomerStage
}: CustomerChargesTableMapperProps) => ({
  totalCount: data?.meta?.total_count,
  rows: data?.records?.map((record) => {
    const isScheduledNfseClickable =
      record?.nfse_status === 'scheduled' && !!record?.nfse_scheduled?.uuid
    const isIssuedNfseClickable =
      !!record?.nfse?.uuid &&
      record?.nfse_status !== 'not_scheduled' &&
      record?.nfse_status !== 'scheduled'
    const isNfseClickable = isScheduledNfseClickable || isIssuedNfseClickable

    const baseRow = {
      uuid: record?.uuid,
      ...(customerPage
        ? {}
        : {
            client: (
              <Box display="flex" alignItems="center" gap="11px">
                <CustomerAvatar
                  name={record?.customer?.name}
                  logoUrl={(record?.customer as any)?.logo_url}
                />
                <p>{record?.customer?.name}</p>{' '}
                <span style={{ color: '#777877', fontSize: '12px' }}>
                  {record?.customer?.nickname}
                </span>
                {showCustomerStage && record?.customer?.category && (
                  <S.CustomerStage stage={record.customer.category}>
                    {getCustomerStageLabel(record.customer.category)}
                  </S.CustomerStage>
                )}
              </Box>
            )
          }),
      dueDate: <p>{formatDate(record?.due_date)}</p>,
      ...(isScheduledTab
        ? {}
        : {
            paymentDate: <p>{formatDate(record?.date_paid || '')}</p>
          }),
      amountBilled: <p>{formatMoney(record?.amount_billed)}</p>,
      ...(isScheduledTab
        ? {}
        : {
            amountPaid: (
              <p>
                {record?.amount_paid != '0.0'
                  ? formatMoney(record?.amount_paid)
                  : ''}
              </p>
            )
          }),
      status: (
        <S.Status status={record?.status} dueDate={record.due_date}>
          {getStatusOptionCharge(record?.status, record?.due_date).label}
        </S.Status>
      ),
      actions: (
        <Box
          display="flex"
          justifyContent="flex-end"
          width="100%"
          onClick={(e) => e.stopPropagation()}
        >
          {!actions?.notViewActions && (
            <>
              <Can permission="nfses:show">
                <Tooltip
                  enterTouchDelay={0}
                  arrow={true}
                  placement="top"
                  title={
                    getStatusOption(record?.nfse_status, record?.nfse_policy)
                      .label
                  }
                >
                  <Box
                    sx={{
                      cursor: isNfseClickable ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (!actions.toggleModals) return
                      if (isScheduledNfseClickable) {
                        actions.toggleModals({
                          modal: 'isOpenDetailInvoice',
                          nfseScheduledUuid: record.nfse_scheduled?.uuid
                        })
                      } else if (isIssuedNfseClickable) {
                        actions.toggleModals({
                          modal: 'isOpenDetailInvoice',
                          uuid: record.nfse.uuid
                        })
                      }
                    }}
                  >
                    <ButtonRounded
                      variant="transparent"
                      disabled={!isNfseClickable}
                    >
                      <InvoiceIcon
                        color={
                          getStatusOption(
                            record?.nfse_status,
                            record?.nfse_policy
                          ).color
                        }
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>

              <Can permission="recurrences:show">
                <Tooltip
                  arrow={true}
                  placement="top"
                  title={
                    !record?.recurrence_uuid
                      ? 'Cobrança única'
                      : 'Visualizar recorrência'
                  }
                >
                  <Box
                    sx={{
                      cursor: !record?.recurrence_uuid ? 'default' : 'pointer'
                    }}
                  >
                    <ButtonRounded
                      disabled={!record?.recurrence_uuid}
                      onClick={() =>
                        actions.toggleModals &&
                        actions.toggleModals({
                          modal: 'isOpenModalRecurrenceDetail',
                          uuid: record.recurrence_uuid
                        })
                      }
                      variant="transparent"
                    >
                      <Icon
                        icon="cached"
                        sx={{
                          color:
                            theme.palette.neutral[
                              !record?.recurrence_uuid ? 700 : 200
                            ]
                        }}
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>

              <Can permission="billings:show">
                <Tooltip
                  enterTouchDelay={0}
                  arrow={true}
                  placement="top"
                  title="Visualizar fatura"
                >
                  <Box>
                    <ButtonRounded
                      disabled={
                        getStatusOptionCharge(record.status || '').disabled
                      }
                      onClick={() => {
                        window.open(getInvoiceUrl(record.uuid), '_blank')
                      }}
                      variant="transparent"
                    >
                      <Icon
                        icon="receipt"
                        sx={{
                          color: getStatusOptionCharge(record.status || '')
                            .disabled
                            ? theme.palette.neutral[700]
                            : theme.palette.neutral[200]
                        }}
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>

              <Can permission="billings:show">
                {/* SC-10890: sem onClick de propósito. Este mapper alimenta a
                    tabela de seleção dentro do ModalInvoice — abrir o detalhe da
                    cobrança daqui seria modal sobre modal. O indicador cai em
                    disabled e o comportamento fica igual ao de antes. */}
                <InvoiceOpenIndicator
                  variant="row"
                  viewed={record.viewed}
                  viewsCount={record.open_count}
                  lastViewedAt={record.last_opened_at}
                />
              </Can>

              <Can permission="billings:show">
                <Tooltip
                  enterTouchDelay={0}
                  arrow={true}
                  placement="top"
                  title="Detalhes cobrança"
                >
                  <Box>
                    <ButtonRounded
                      onClick={() =>
                        actions.toggleModals &&
                        actions.toggleModals({
                          modal: 'isOpenModalChargeDetail',
                          uuid: record.uuid
                        })
                      }
                      variant="transparent"
                      data-cy="open-billing"
                    >
                      <Icon
                        icon="settings"
                        sx={{ color: theme.palette.neutral[200] }}
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>
            </>
          )}
        </Box>
      )
    }
    return baseRow
  })
})

export type CustomerCharges = ReturnType<typeof customerChargesTableMapper>

export const statisticsMapper = (statistics: StatisticsMapperParams) => {
  const {
    total_billed,
    total_count,
    total_paid,
    total_received,
    total_to_receive,
    total_not_due,
    total_churn,
    total_due_up_to_7_days,
    total_due_more_than_7_days,
    total_leads,
    // Novos campos hierárquicos da API (opcionais)
    leads: leadsBreakdown,
    customers: customersBreakdown,
    // Breakdown do recebido
    total_received_original,
    total_received_fine_and_fee,
    total_received_discount,
    total_received_bonification
  } = statistics

  // Mock data se a API ainda não retornar os campos hierárquicos
  // Divide proporcionalmente os valores existentes
  const mockLeadsBreakdown = {
    total: total_leads || 0,
    not_due: (total_not_due || 0) * 0.3, // 30% para leads
    due_up_to_7_days: (total_due_up_to_7_days || 0) * 0.3,
    due_more_than_7_days: (total_due_more_than_7_days || 0) * 0.3,
    churn: (total_churn || 0) * 0.3
  }

  const mockCustomersBreakdown = {
    total: (total_to_receive || 0) - (total_leads || 0) - (total_churn || 0),
    not_due: (total_not_due || 0) * 0.7, // 70% para clientes
    due_up_to_7_days: (total_due_up_to_7_days || 0) * 0.7,
    due_more_than_7_days: (total_due_more_than_7_days || 0) * 0.7,
    churn: (total_churn || 0) * 0.7
  }

  // Usa dados da API se disponíveis, senão usa mock
  const leads = leadsBreakdown || mockLeadsBreakdown
  const customers = customersBreakdown || mockCustomersBreakdown

  return {
    standardCards: [
      {
        title: 'Faturas / pagas',
        subtitle: `${total_count} / ${total_paid}`
      },
      {
        title: 'Recebido',
        subtitle: formatMoney(total_received),
        filter: {
          name: 'received',
          value: 'true'
        },
        actionIcon: 'faChevronDown',
        tooltipAction: 'Expandir detalhes'
        // action será adicionado no Charge.tsx
      },
      {
        title: 'Previsto',
        subtitle: formatMoney(total_to_receive),
        filter: {
          name: 'to_receive',
          value: 'true'
        },
        actionIcon: 'faChevronDown',
        tooltipAction: 'Expandir detalhes'
        // action será adicionado no Charge.tsx
      },
      {
        title: 'Total',
        subtitle: formatMoney(total_billed)
      }
    ],
    // Segunda linha do Recebido: Valor original, Juros/Multa, Descontos, Bonificações
    receivedCards: [
      {
        title: 'Valor original',
        subtitle: formatMoney(total_received_original ?? 0)
      },
      {
        title: 'Juros/Multa',
        subtitle: formatMoney(total_received_fine_and_fee ?? 0)
      },
      {
        title: 'Descontos',
        subtitle: formatMoney(total_received_discount ?? 0)
      },
      {
        title: 'Bonificações',
        subtitle: formatMoney(total_received_bonification ?? 0)
      }
    ],
    // Segunda linha do Previsto: Não vencidos, Vencidos até 7 dias, Vencidos há mais de 7 dias, Canceladas/Expiradas
    toReceiveCards: [
      {
        title: 'Não vencidos',
        subtitle: formatMoney(total_not_due),
        filter: {
          name: 'not_due',
          value: 'true'
        },
        leadsClientesBreakdown: {
          leads: leads.not_due,
          customers: customers.not_due,
          filterType: 'not_due',
          cardTitle: 'não vencidos'
        }
      },
      {
        title: 'Vencidos até 7 dias',
        subtitle: formatMoney(total_due_up_to_7_days),
        filter: {
          name: 'overdue_less_than_7_days',
          value: 'true'
        },
        leadsClientesBreakdown: {
          leads: leads.due_up_to_7_days,
          customers: customers.due_up_to_7_days,
          filterType: 'overdue_less_than_7_days',
          cardTitle: 'vencidos até 7 dias'
        }
      },
      {
        title: 'Vencidos há mais de 7 dias',
        subtitle: formatMoney(total_due_more_than_7_days),
        filter: {
          name: 'overdue_more_than_7_days',
          value: 'true'
        },
        leadsClientesBreakdown: {
          leads: leads.due_more_than_7_days,
          customers: customers.due_more_than_7_days,
          filterType: 'overdue_more_than_7_days',
          cardTitle: 'vencidos há mais de 7 dias'
        }
      },
      {
        title: 'Canceladas/Expiradas',
        subtitle: formatMoney(total_churn),
        filter: {
          name: 'churn',
          value: 'true'
        },
        leadsClientesBreakdown: {
          leads: leads.churn,
          customers: customers.churn,
          filterType: 'churn',
          cardTitle: 'canceladas/expiradas'
        }
      }
    ]
  } as {
    standardCards: Info[]
    receivedCards: Info[]
    toReceiveCards: Info[]
  }
}

type StatisticsTransfersMapper = {
  total_count: number
  amount_total_failed: string
  amount_total_pending: string
  amount_total_received: string
}

export const statisticsTransfersMapper = (
  statistics: StatisticsTransfersMapper
) => {
  const {
    total_count,
    amount_total_failed,
    amount_total_pending,
    amount_total_received
  } = statistics

  return [
    {
      title: 'Número de transferências',
      subtitle: `${total_count}`
    },
    {
      title: 'Total pendente',
      subtitle: formatMoney(amount_total_pending),
      filter: {
        name: 'status',
        value: 'pending|processing'
      }
    },
    {
      title: 'Total confirmado',
      subtitle: formatMoney(amount_total_received),
      filter: {
        name: 'status',
        value: 'succeeded|confirmed'
      }
    },
    {
      title: 'Total cancelado',
      subtitle: formatMoney(amount_total_failed),
      filter: {
        name: 'status',
        value: 'failed|canceled'
      }
    }
  ] as Info[]
}
